/**
 * §5 — the Case Register: the stage key strip, the hint block, the seven columns, the count
 * line, the empty state and the panel foot.
 *
 * **The stage counts are of the whole register, and that decides where the filter runs.** §5
 * puts a count badge on every one of the five stage buttons, so the four the TD is not
 * looking at have to keep counting cases the fifth has hidden. A server-side `?stage=` filter
 * would return one stage's rows and leave the other four badges counting nothing. So the page
 * fetches the competition's whole register and this component narrows it — which also gives
 * §5's count line its honest denominator: `{n} Of {m} Cases Shown`, where `m` is everything
 * the competition holds and not everything that survived the filter.
 *
 * **Clicking the active stage clears to all**, exactly as §5 says, so the strip is a filter
 * and never a trap.
 *
 * **A row is a button, not a link.** §8's Case File is a panel on this page rather than a
 * route, so a row opens something already on screen; §5 asks for `tabindex="0"` with click or
 * Enter, and this build adds Space for the same reason the roster's name is a real link —
 * the control should behave like what it is.
 */

import {
  HEARING_STAGES,
  HEARING_STAGE_LABELS,
  HEARING_STAGE_TOOLTIPS,
  type HearingStage,
  type TmsCompetitionId,
} from "@hockeytd/shared";

import { CountryFlag } from "../../app/CountryFlag.js";
import type { HearingCaseView } from "../../api/types.js";
import {
  caseLevel,
  matchRefText,
  minimumPenaltyTitle,
  outcomeText,
  personRoleShort,
} from "./caseText.js";

export type CompetitionScope = "all" | TmsCompetitionId;

/** §5's hint block, verbatim. */
export function StageKeyHint(): React.JSX.Element {
  return (
    <div className="hidenote" data-testid="stageKeyHint">
      <b>Stage key — click any stage to filter the register.</b> The stages follow the working
      procedure: the TD decides a case is worth a hearing and creates it, the Hearing Letter
      goes to the Team Manager, the hearing is chaired by the TD with an assistant taking the
      minutes, and the written Decision is presented to the Team Manager and the person
      involved. Level 1 carries a minimum of an official reprimand or a suspension of at least
      one match; Level 2 a minimum of two matches; Level 3 a minimum of five.
    </div>
  );
}

export interface CaseRegisterPanelProps {
  /** Every case in the competition scope, before the stage filter. */
  hearings: readonly HearingCaseView[];
  stage: HearingStage | null;
  onSelectStage: (stage: HearingStage | null) => void;
  competition: CompetitionScope;
  onSelectCompetition: (competition: CompetitionScope) => void;
  selectedId: string | null;
  onSelectCase: (id: string) => void;
  onCreate: () => void;
}

export function CaseRegisterPanel({
  hearings,
  stage,
  onSelectStage,
  competition,
  onSelectCompetition,
  selectedId,
  onSelectCase,
  onCreate,
}: CaseRegisterPanelProps): React.JSX.Element {
  const shown = stage === null ? hearings : hearings.filter((one) => one.stage === stage);

  return (
    <section
      className="panel panel--d1"
      aria-labelledby="registerTitle"
      style={{ marginTop: 18 }}
      data-howto="case-register"
    >
      <div className="panel__head">
        <h3 className="panel__title" id="registerTitle">
          Case Register
        </h3>
        <span className="panel__note">Click Any Case To Open Its File Below</span>
        <span className="spacer" />
        <span className="kicker">Competition</span>
        <div className="roletabs" role="group" aria-label="Competition filter">
          {(
            [
              ["all", "All"],
              ["1916", "Men's"],
              ["1917", "Women's"],
            ] as const
          ).map(([value, label]) => (
            <button
              key={value}
              type="button"
              className="roletab"
              aria-pressed={competition === value}
              onClick={() => {
                onSelectCompetition(value);
              }}
            >
              {label}
            </button>
          ))}
        </div>
        <button
          type="button"
          className="gbtn gbtn--solid"
          data-howto="create-hearing"
          onClick={onCreate}
        >
          + Create New Hearing
        </button>
      </div>

      {/* The five stage toggles, each a chip and a count badge. The chip word and its
          tooltip are `@hockeytd/shared`'s, which is the same copy the API reads. */}
      <div className="roletabs stagekey" role="group" aria-label="Stage filter">
        {HEARING_STAGES.map((key) => {
          const count = hearings.filter((one) => one.stage === key).length;
          return (
            <button
              key={key}
              type="button"
              className="roletab"
              aria-pressed={stage === key}
              title={HEARING_STAGE_TOOLTIPS[key]}
              data-testid={`stageKey-${key}`}
              onClick={() => {
                onSelectStage(stage === key ? null : key);
              }}
            >
              {HEARING_STAGE_LABELS[key]} <span className="stagekey__n">{count}</span>
            </button>
          );
        })}
      </div>

      <StageKeyHint />

      <table className="mtable sumtable casetable" data-testid="registerTable">
        <thead>
          <tr>
            <th scope="col">Case #</th>
            <th scope="col">Match Ref</th>
            <th scope="col">Team</th>
            <th scope="col">Persons Involved</th>
            <th scope="col">Charges</th>
            <th scope="col">Stage</th>
            <th scope="col">Outcome</th>
          </tr>
        </thead>
        <tbody>
          {shown.length === 0 ? (
            <tr>
              <td colSpan={7}>
                <div className="emptystate" data-testid="registerEmpty">
                  <b>No Cases Match This Filter</b>
                  Clear the stage filter or switch competition to see the rest of the register.
                </div>
              </td>
            </tr>
          ) : (
            shown.map((hearing) => {
              const level = caseLevel(hearing.charges);
              const outcome = outcomeText(hearing);
              return (
                <tr
                  key={hearing.id}
                  tabIndex={0}
                  role="button"
                  aria-pressed={selectedId === hearing.id}
                  className={selectedId === hearing.id ? "is-sel" : undefined}
                  data-testid={`caseRow-${hearing.caseNumber}`}
                  onClick={() => {
                    onSelectCase(hearing.id);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      onSelectCase(hearing.id);
                    }
                  }}
                >
                  <td className="c-no">{hearing.caseNumber}</td>
                  <td className="c-match">{matchRefText(hearing)}</td>
                  <td className="cell-ctry">
                    {hearing.teamCode === null ? "—" : <CountryFlag code={hearing.teamCode} />}
                  </td>
                  {/*
                   * THE FIRST THING THE TECHNICAL DELEGATE LOOKS FOR IS THE COUNTRY, so the
                   * flag is beside the person as well as in the Team column — a register is
                   * scanned down the Persons Involved cell, not across the row.
                   *
                   * The person carries the CASE's team code: a hearing person belongs to the
                   * team the case is against, and nothing on the record gives them another
                   * one. Where the case names no team, no flag is drawn rather than a guess.
                   */}
                  <td className="c-subj">
                    {hearing.personsInvolved.map((person) => (
                      <span key={`${person.name}:${person.role}`} className="hperson">
                        {hearing.teamCode === null ? null : (
                          <CountryFlag code={hearing.teamCode} className="hperson__flag" />
                        )}
                        <b className="hperson__name">{person.name}</b>
                        <span className="hperson__role">· {personRoleShort(person)}</span>
                      </span>
                    ))}
                  </td>
                  <td className="c-charge">
                    {hearing.charges.map((charge) => (
                      <span key={charge.article} className="tbschip">
                        Art {charge.article}
                      </span>
                    ))}
                    {level === null ? null : (
                      <span
                        className={`lvchip lvchip--${String(level)}`}
                        title={minimumPenaltyTitle(level)}
                      >
                        Level {level}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className="statuspill"
                      title={HEARING_STAGE_TOOLTIPS[hearing.stage]}
                    >
                      <span className="sdot" aria-hidden="true" />
                      {HEARING_STAGE_LABELS[hearing.stage]}
                    </span>
                  </td>
                  {/* The mockup bolds a decided outcome and greys every other, so the eye
                      finds the cases that are finished without reading the words. */}
                  <td
                    className="outcome"
                    {...(outcome.title === undefined ? {} : { title: outcome.title })}
                  >
                    {hearing.stage === "decision" ? (
                      <b>{outcome.text}</b>
                    ) : (
                      <span className="none">{outcome.text}</span>
                    )}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>

      <p className="countline" data-testid="registerCount">
        {shown.length} Of {hearings.length} Cases Shown
      </p>

      <div className="panel__foot">
        <b>
          Any person may report an incident to the Technical Delegate, and the TD may act on
          his own observation.
        </b>{" "}
        The TD decides whether the matter is worth a Code of Conduct hearing, determines the
        article and the level, and creates the case here. A report received more than 24 hours
        after the conduct may only be accepted at the TD&rsquo;s discretion, with the reasons
        stated in the Hearing Letter.
      </div>
    </section>
  );
}
